/**
 * Bottom navigation — Play / Wallet / Ranks / Profile.
 *
 * Wireframe convention: visible on every authenticated, full-app screen.
 * Hidden on auth screens (login/signup/onboarding), live gameplay (where
 * a back-button is the only nav), and admin web tools.
 *
 * Active state is derived from the current pathname rather than a prop so
 * we can re-render in place without the route handler having to think.
 */

import { applyFriendsBadge } from '../lib/friends_badge.js';
import { icon, I } from './icons.js';

const TABS = [
  { key: 'play', icon: I.play, label: 'Play', match: (p) => p === '/' || p.startsWith('/play') || p.startsWith('/match') || p.startsWith('/contest') || p.startsWith('/live') },
  { key: 'friends', icon: I.users, label: 'Friends', match: (p) => p.startsWith('/friends') || p.startsWith('/challenges') },
  { key: 'wallet', icon: I.wallet, label: 'Wallet', match: (p) => p.startsWith('/wallet') },
  { key: 'ranks', icon: I.trophy, label: 'Ranks', match: (p) => p.startsWith('/ranks') },
  { key: 'profile', icon: I.user, label: 'Profile', match: (p) => p.startsWith('/profile') || p === '/me' || p.startsWith('/interests') || p.startsWith('/kyc') || p.startsWith('/admin') },
];

const HREF = {
  play: '/',
  friends: '/friends',
  wallet: '/wallet',
  ranks: '/ranks',
  profile: '/profile',
};

export function renderTabbar(host, path) {
  host.innerHTML = `
    <nav class="tabbar" aria-label="Primary">
      ${TABS.map((t) => {
        const on = t.match(path);
        // data-key lets the CSS pick out the wallet tab for the gold
        // treatment without touching the icon component.
        // Expose a `data-tour` hook on the Wallet tab so the home-screen
        // first-run tour can spotlight it. Other tabs don't need one.
        const tourAttr = t.key === 'wallet' ? ' data-tour="wallet-tab"' : '';
        // Friends tab gets a notification badge anchored to the icon —
        // friends_badge.js owns the count + visibility.
        const badge = t.key === 'friends'
          ? '<span id="tab-badge-friends" class="tab-badge" style="display:none;"></span>'
          : '';
        return `<a href="${HREF[t.key]}" data-link data-key="${t.key}"${tourAttr} class="${on ? 'on' : ''}" aria-current="${on ? 'page' : 'false'}">
          <span class="e" style="position:relative;">${icon(t.icon, { size: 21 })}${badge}</span>${t.label}
        </a>`;
      }).join('')}
    </nav>
  `;
  // Re-paint any cached friends badge count — the route change just
  // blew away the previous DOM node.
  applyFriendsBadge();
}

/** Routes where the tab bar is hidden (auth screens, live gameplay). */
export function shouldHideTabbar(path) {
  if (path === '/onboarding') return true;
  if (path === '/signup' || path === '/login') return true;
  if (path === '/forgot-password' || path === '/reset-password') return true;
  if (path === '/verify-email') return true;
  // Active match: keep focus on the question.
  if (/^\/match\/[^/]+$/.test(path)) return true;
  // Phase 8.12 — active async duel run. The status page (/duel/<id>)
  // still shows the tabbar; only the run itself (/duel/<id>/play) is
  // immersive.
  if (/^\/duel\/[^/]+\/play$/.test(path)) return true;
  // Adjudication re-test — same argument as live/duel: focus on the
  // question. The intro and post-submit states hide the tabbar too;
  // that's fine, back-arrow in the appbar navigates home.
  if (path === '/retest') return true;
  // Phase 8.12 — the messaging composer pins to the bottom of the
  // viewport; the tab bar would sit on top of the input. Applies to
  // both an existing thread (/messages/<conv_id>) and compose mode
  // (/messages/new/<user_id>). The inbox itself (/messages,
  // /messages/requests) still shows the tabbar.
  if (path.startsWith('/messages/') && path !== '/messages/requests') return true;
  // Admin v2 owns its own desktop chrome; the phone tabbar makes no sense there.
  if (path === '/admin/v2' || path.startsWith('/admin/v2/')) return true;
  return false;
}
